import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import {
  Button,
  getVoiceOrbState,
  TheySaidAiLogoIcon,
  VoiceOrb,
  VoiceOrbDock,
} from '@/shared-ui';

import type {
  HandsFreeFooterValue,
  HandsFreeQuestionPanelValue,
} from './project-usability-handsfree-content.types';
import { HandsFreePopupSelectionProvider } from './project-usability-handsfree-popup-context';
import ProjectUsabilityHandsFreeProgressBars from './project-usability-handsfree-progress-bars';
import ProjectUsabilityHandsFreeQuestionPopup from './project-usability-handsfree-question-popup';

interface Props extends HandsFreeFooterValue, HandsFreeQuestionPanelValue {
  orbState: ReturnType<typeof getVoiceOrbState>;
  effectiveAudioLevel: number;
}

export default function ProjectUsabilityHandsFreeFooter({
  orbState,
  effectiveAudioLevel,
  showSubmitted,
  isStuckListening,
  isOnboardingActive,
  onRestoreListening,
  onPlayPauseClick,
  showPlayIcon,
  playPauseAriaLabel,
  effectiveHelpExpanded,
  onHelpExpandedChange,
  isSessionOver,
  progress,
  effectivePopupOpen,
  isPopupOpen,
  togglePopup,
  onClose,
  onMouseEnter,
  onMouseLeave,
  canCloseTaskPanel,
  currentQuestionIndex,
  totalQuestions,
  lastAssistantMessage,
  replyFeedbackMessage,
  projectType,
  onReportProblemSubmit,
  effectiveIsConversationOver,
  onConversationOver,
  conversationOverText,
  onSubmitContent,
  isSubmitting,
  popupSelectionValue,
  onPopupSelectionChange,
  compactOrb,
}: Props) {
  const t = useTranslations('Usability');

  return (
    <footer className='relative z-30 flex h-20 shrink-0 items-center justify-between bg-neutral-950 px-4 sm:px-6'>
      <div className='hidden shrink-0 flex-col sm:flex' aria-hidden='true'>
        <span className='text-[10px]/4.5 font-normal text-gray-400'>
          {t('poweredBy')}
        </span>
        <TheySaidAiLogoIcon />
      </div>

      <VoiceOrbDock
        className={cn(
          'pointer-events-auto absolute left-1/2 -translate-x-1/2',
          compactOrb ? 'top-1/2 -translate-y-1/2' : 'top-0 translate-y-[-46.5%]'
        )}
        handlePlayPauseClick={onPlayPauseClick}
        showPlayIcon={showPlayIcon}
        playPauseAriaLabel={playPauseAriaLabel}
        isHelpExpanded={effectiveHelpExpanded}
        onHelpExpandedChange={onHelpExpandedChange}
        isConversationOver={isSessionOver}
        compact={compactOrb}
      >
        <VoiceOrb
          state={orbState}
          audioLevel={effectiveAudioLevel}
          progress={progress}
          compact={compactOrb}
        />
      </VoiceOrbDock>

      {isStuckListening && !isOnboardingActive && (
        <div
          className='pointer-events-auto absolute top-0 left-1/2 z-30 flex -translate-x-1/2 translate-y-[-120%] items-center gap-2 rounded-full bg-red-600 px-4 py-2.5 shadow-lg motion-safe:animate-pulse'
          data-test='restore-listening-prompt'
        >
          <span className='text-sm font-medium whitespace-nowrap text-white'>
            {t('havingTroubleListening')}
          </span>
          <Button
            variant='ghost'
            size='sm'
            className='shrink-0 rounded-full bg-white/30 font-medium text-white hover:enabled:bg-white/40'
            onClick={onRestoreListening}
            aria-label={t('restoreListeningAria')}
            data-test='restore-listening-button'
          >
            {t('tapToRestore')}
          </Button>
        </div>
      )}

      {!showSubmitted ? (
        <div className='pointer-events-auto relative ml-auto shrink-0'>
          {effectivePopupOpen && lastAssistantMessage && (
            <HandsFreePopupSelectionProvider
              value={popupSelectionValue}
              onChange={onPopupSelectionChange}
            >
              <ProjectUsabilityHandsFreeQuestionPopup
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={totalQuestions}
                message={lastAssistantMessage}
                projectType={projectType}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                <ProjectUsabilityHandsFreeQuestionPopup.Header
                  onClose={onClose}
                  disabled={!canCloseTaskPanel}
                />
                <ProjectUsabilityHandsFreeQuestionPopup.Body
                  replyFeedbackMessage={replyFeedbackMessage}
                  isConversationOver={effectiveIsConversationOver}
                  isSubmitting={isSubmitting}
                  onSubmitContent={onSubmitContent}
                  onReportProblemSubmit={onReportProblemSubmit}
                />
                {effectiveIsConversationOver && onConversationOver && (
                  <ProjectUsabilityHandsFreeQuestionPopup.Footer
                    onConversationOver={onConversationOver}
                    conversationOverText={conversationOverText}
                  />
                )}
              </ProjectUsabilityHandsFreeQuestionPopup>
            </HandsFreePopupSelectionProvider>
          )}
          <ProjectUsabilityHandsFreeProgressBars
            totalQuestions={totalQuestions}
            currentQuestionIndex={currentQuestionIndex}
            isPopupOpen={isPopupOpen}
            onActiveBarClick={togglePopup}
          />
        </div>
      ) : (
        <div className='pointer-events-auto relative ml-auto shrink-0' />
      )}
    </footer>
  );
}
